import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { ScrollText, CheckCircle2, XCircle, Trash2, Download, X, User } from "lucide-react";

/**
 * Planșe de arhitectură (admin side).
 * Frații încarcă planșe din aplicația mobilă (status: propusă). Adminul le
 * descarcă, le citește și decide: APROBATĂ (devine vizibilă fraților, pe
 * grade) sau NEAPROBATĂ (cu motiv + sugestii — fratele primește notificare
 * push și poate modifica/reîncărca planșa din aplicația mobilă).
 */
export default function PlansePage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/planse");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const degLabel = (d) => t(`planse.deg${Math.min(Math.max(d || 1, 1), 3)}`);
  const fmtD = (iso) => (iso || "").slice(0, 10);

  const approve = async (p) => {
    setBusy(true);
    try {
      await api.post(`/planse/${p.id}/approve`);
      toast.success(t("planse.approvedOk"));
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const sendReject = async () => {
    if (!reason.trim()) { toast.error(t("planse.reasonRequired")); return; }
    setBusy(true);
    try {
      await api.post(`/planse/${rejectFor.id}/reject`, { reason: reason.trim() });
      toast.success(t("planse.rejectedOk"));
      setRejectFor(null);
      setReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`${t("planse.deleteConfirm")} «${p.title}»?`)) return;
    try {
      await api.delete(`/planse/${p.id}`);
      toast.success(t("common.deleted", "Șters"));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (p) => {
    try {
      const res = await api.get(`/planse/${p.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = p.file_name || "plansa.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pending = rows.filter((p) => ["proposed", "scheduled"].includes(p.status));
  const approved = rows.filter((p) => ["approved", "presented"].includes(p.status));
  const rejected = rows.filter((p) => p.status === "rejected");

  // Approved pieces grouped by brother, brothers in alphabetical order.
  const byAuthor = {};
  approved.forEach((p) => {
    const k = p.author_name || "—";
    if (!byAuthor[k]) byAuthor[k] = [];
    byAuthor[k].push(p);
  });
  const authors = Object.keys(byAuthor).sort((a, b) => a.localeCompare(b, "ro"));

  return (
    <div data-testid="planse-page">
      <div>
        <h1 className="page-title flex items-center gap-2">
          <ScrollText size={22} /> {t("planse.title")}
        </h1>
        <p className="page-subtitle">{t("planse.subtitle")}</p>
      </div>

      {/* ===== Pending approval ===== */}
      <h2 className="mt-8" style={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: 1, textTransform: "uppercase", color: "#8a7340" }}>
        {t("planse.pendingTitle")} {pending.length > 0 && <span className="badge badge-gold">{pending.length}</span>}
      </h2>
      {pending.length === 0 && (
        <p className="mt-2" style={{ color: "#8a8470", fontStyle: "italic", fontSize: "0.85rem" }}>{t("planse.emptyPending")}</p>
      )}
      <div className="mt-3 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}>
        {pending.map((p) => (
          <div key={p.id} className="lodge-card" style={{ padding: 16 }} data-testid={`plansa-pending-${p.id}`}>
            <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>{p.title}</div>
            <div style={{ fontSize: "0.78rem", color: "#6b6557", marginTop: 4 }}>
              {t("planse.colAuthor")}: <b>{p.author_name}</b> · {degLabel(p.degree)} · {fmtD(p.created_at)}
            </div>
            {p.description && <p style={{ fontSize: "0.8rem", color: "#6b6557", marginTop: 6 }}>{p.description}</p>}
            {p.resubmitted_at && (
              <div className="badge badge-muted mt-2" data-testid={`plansa-resubmitted-${p.id}`}>{t("planse.resubmitted")}</div>
            )}
            <div className="flex gap-2 mt-4 flex-wrap">
              <button className="btn-outline-gold flex items-center gap-1" onClick={() => download(p)} data-testid={`download-btn-${p.id}`}>
                <Download size={13} /> {t("common.download", "Descarcă")}
              </button>
              <button
                className="btn-primary-gold flex items-center gap-1"
                onClick={() => approve(p)}
                disabled={busy}
                data-testid={`approve-btn-${p.id}`}
              >
                <CheckCircle2 size={13} /> {t("planse.approveBtn")}
              </button>
              <button
                className="btn-danger flex items-center gap-1"
                onClick={() => { setRejectFor(p); setReason(""); }}
                disabled={busy}
                data-testid={`reject-btn-${p.id}`}
              >
                <XCircle size={13} /> {t("planse.rejectBtn")}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ===== Approved — grouped by brother (alphabetical) ===== */}
      <h2 className="mt-10" style={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: 1, textTransform: "uppercase", color: "#8a7340" }}>
        {t("planse.approvedTitle")}
      </h2>
      {authors.length === 0 && (
        <p className="mt-2" style={{ color: "#8a8470", fontStyle: "italic", fontSize: "0.85rem" }}>{t("planse.emptyApproved")}</p>
      )}
      {authors.map((name) => (
        <div key={name} className="mt-5" data-testid={`author-group-${name}`}>
          <div className="flex items-center gap-2" style={{ fontWeight: 700, fontSize: "0.9rem", color: "#0d1b2a" }}>
            <User size={15} /> {name}
            <span style={{ fontSize: "0.72rem", color: "#8a8470", fontWeight: 400 }}>
              · {byAuthor[name].length} {t("planse.piecesCount")}
            </span>
          </div>
          <div className="mt-2 flex gap-3 flex-wrap">
            {byAuthor[name]
              .slice()
              .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
              .map((p) => (
                <div key={p.id} className="lodge-card" style={{ padding: 12, width: 250 }} data-testid={`plansa-approved-${p.id}`}>
                  <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{p.title}</div>
                  <div style={{ fontSize: "0.72rem", color: "#8a8470", marginTop: 3 }}>
                    {degLabel(p.degree)} · {fmtD(p.approved_at || p.presented_at || p.created_at)}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button className="btn-outline-gold" title={t("common.download", "Descarcă")} onClick={() => download(p)} data-testid={`download-approved-${p.id}`}>
                      <Download size={12} />
                    </button>
                    <button className="btn-danger" title={t("common.delete")} onClick={() => remove(p)} data-testid={`delete-btn-${p.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}

      {/* ===== Rejected ===== */}
      {rejected.length > 0 && (
        <>
          <h2 className="mt-10" style={{ fontWeight: 700, fontSize: "0.95rem", letterSpacing: 1, textTransform: "uppercase", color: "#a14034" }}>
            {t("planse.rejectedTitle")}
          </h2>
          <div className="mt-3 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}>
            {rejected.map((p) => (
              <div key={p.id} className="lodge-card" style={{ padding: 16, opacity: 0.85 }} data-testid={`plansa-rejected-${p.id}`}>
                <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{p.title}</div>
                <div style={{ fontSize: "0.75rem", color: "#6b6557", marginTop: 4 }}>
                  {p.author_name} · {degLabel(p.degree)} · {fmtD(p.rejected_at || p.created_at)}
                </div>
                {p.review_note && (
                  <p style={{ fontSize: "0.78rem", color: "#a14034", marginTop: 6, fontStyle: "italic" }}>
                    „{p.review_note}"
                  </p>
                )}
                <div className="flex gap-2 mt-3">
                  <button className="btn-outline-gold" onClick={() => download(p)} data-testid={`download-rejected-${p.id}`}>
                    <Download size={12} />
                  </button>
                  <button className="btn-danger" onClick={() => remove(p)} data-testid={`delete-rejected-${p.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ===== Reject modal — reason + suggestions for the brother ===== */}
      {rejectFor && (
        <div className="dialog-overlay" onClick={() => setRejectFor(null)}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }} data-testid="reject-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title">{t("planse.rejectModalTitle")}</h2>
              <button className="btn-outline-gold" onClick={() => setRejectFor(null)}><X size={14} /></button>
            </div>
            <p style={{ fontSize: "0.85rem", marginBottom: 6 }}>
              «{rejectFor.title}» — {rejectFor.author_name}
            </p>
            <p style={{ fontSize: "0.75rem", color: "#8a8470", marginBottom: 10 }}>{t("planse.rejectHint")}</p>
            <textarea
              className="input-lodge"
              rows={5}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("planse.rejectPlaceholder")}
              data-testid="reject-reason-input"
            />
            <div className="flex justify-end gap-3 mt-5">
              <button className="btn-outline-gold" onClick={() => setRejectFor(null)}>{t("common.cancel")}</button>
              <button className="btn-danger" onClick={sendReject} disabled={busy} data-testid="reject-send">
                {t("planse.rejectSend")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
